import {
  create,
  send,
  unobserveOrCancel,
  toObservable,
} from "@absinthe/socket";
import type { AbsintheSocket, Notifier, Observer } from "@absinthe/socket";
import { useEnvironmentInterface } from "environment-interface";
import { env } from "environment-interface/env";
import { Socket as PhoenixSocket } from "phoenix";
import type { SocketConnectOption } from "phoenix";
import { useEffect, useState } from "react";
import type { Client } from "urql";
import {
  Provider,
  createClient,
  defaultExchanges,
  subscriptionExchange,
} from "urql";

import { useSession } from "./api/useSession";

const unobserveOrCancelIfNeeded = (
  absintheSocket: AbsintheSocket,
  notifier: Notifier | undefined,
  observer: Observer | undefined,
) => {
  if (notifier && observer) {
    unobserveOrCancel(absintheSocket, notifier, observer);
  }
};

const API_URL = new URL(`${env.PUBLIC_API_DOMAIN}/api/graphql`);
const WEBSOCKET_URL = new URL(`${env.PUBLIC_API_DOMAIN}/socket`);

const useUrqlClient = (): Client | undefined => {
  const [client, setClient] = useState<Client>();
  const [session] = useSession();

  const { api } = useEnvironmentInterface();

  const authenticated = session.state === "AUTHENTICATED";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!authenticated) {
      return;
    }

    let phoenixSocket: PhoenixSocket | undefined;

    api.getJwtToken().then((token) => {
      /**
       * Handle websocket protocol if the API domain is
       * served locally.
       */
      const webSocketProtocol =
        WEBSOCKET_URL.protocol === "http:" ? "ws:" : "wss:";
      WEBSOCKET_URL.protocol = webSocketProtocol;

      const phoenixSocketOpts: Partial<SocketConnectOption> = {
        params: { guardian_token: token },
      };

      phoenixSocket = new PhoenixSocket(
        WEBSOCKET_URL.toString(),
        phoenixSocketOpts,
      );
      const absintheSocket = create(phoenixSocket);

      const sameOriginRequest = API_URL.origin === window.location.origin;
      const urqlClient = createClient({
        url: API_URL.toString(),
        fetchOptions: {
          headers: {
            authorization: `Bearer ${token}`,
            ...(sameOriginRequest ? { "x-codesandbox-client": "web" } : {}),
          },
        },
        exchanges: [
          ...defaultExchanges,
          subscriptionExchange({
            // @ts-expect-error: forwardSubscription expects a function
            // with a signature that doesn't match the absinthe socket.
            // It doesn't appear to cause any problems since data is
            // returned as expected.
            forwardSubscription: (operation) => {
              const notifier = send(absintheSocket, {
                operation: operation.query,
                variables: operation.variables,
              });

              return toObservable(absintheSocket, notifier, {
                onStart: (_: Notifier) => ({}),
                onError: async () => {
                  phoenixSocket?.disconnect();
                },
                unsubscribe: unobserveOrCancelIfNeeded,
              });
            },
          }),
        ],
      });

      setClient(urqlClient);
    });

    return () => {
      phoenixSocket?.disconnect();
    };
  }, [authenticated]);

  return client;
};

export const SubscriptionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const client = useUrqlClient();

  if (!client) return <>{children}</>;

  return <Provider value={client}>{children}</Provider>;
};
